import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Home, Users, UsersRound, Shield, MapPin, ArrowLeftRight, CalendarDays, Pill, Syringe, FlaskConical,
  Bell, BarChart3, CloudUpload, Database, Settings, Menu, X, Moon, Sun, LogOut, Eye, UserCircle, Stethoscope, FileText,
  ChevronRight, ChevronDown, ChevronUp, PanelLeftClose, PanelLeftOpen, TestTube, ClipboardList } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const NAV = {
  "/": { perm: "page:dashboard", icon: Home, label: { employee: "Dashboard", supervisor: "Team Dashboard", admin: "Admin Dashboard" } },
  "/patients": { perm: "page:patients", icon: Users, label: { employee: "My Patients", supervisor: "Team Patients", admin: "All Patients" } },
  "/my-dashboard": { perm: "page:dashboard", icon: Home, label: "My Dashboard", supervisorOnly: true },
  "/my-patients": { perm: "page:patients", icon: Users, label: "My Patients", supervisorOnly: true },
  "/assignments": { perm: "page:assignments", icon: ArrowLeftRight, label: "Patient Assignments" },
  "/testing-rotation": { perm: "page:testing_rotation", icon: FileText, label: "Patient Testing Rotation" },
  "/medication-requests": { perm: "page:medication_requests", icon: Pill, label: "Medication Requests" },
  "/appointments": { perm: "page:appointments", icon: CalendarDays, label: "Appointments" },
  "/employees": { perm: "page:employees", icon: UsersRound, label: "Employees" },
  "/roles": { perm: "page:roles", icon: Shield, label: "Supervisors & Roles" },
  "/locations": { perm: "page:locations", icon: MapPin, label: "Locations" },
  "/clinical": { perm: "clinical", icon: Stethoscope, label: "Clinical Management", adminOnly: true },
  "/prescriptions": { perm: "page:prescriptions", icon: ClipboardList, label: "Prescriptions", nonAdmin: true },
  "/injectables": { perm: "page:injectables", icon: Syringe, label: "Injectable PrEP", nonAdmin: true },
  "/labs": { perm: "page:labs", icon: TestTube, label: "Laboratory Monitoring", nonAdmin: true },
  "/tasks": { perm: "page:tasks", icon: FlaskConical, label: "Follow-up Tasks" },
  "/notifications": { perm: "page:notifications", icon: Bell, label: "Notifications" },
  "/reports": { perm: "page:reports", icon: BarChart3, label: { employee: "My Reports", supervisor: "Reports", admin: "Reports & Analytics" } },
  "/import": { perm: "page:import_export", icon: CloudUpload, label: "Import / Export" },
  "/audit": { perm: "page:audit", icon: Database, label: "Audit Logs" },
  "/settings": { perm: "page:settings", icon: Settings, label: "Settings" },
};

const TONES = { none: "", green: "bg-[#E7F6EE] text-emerald-600", lavender: "bg-[#EEEBFB] text-[#4F46C8]", orange: "bg-[#FFF1E2] text-[#EA8A2E]",
  blue: "bg-[#EAF2FF] text-[#2F5BD3]", gray: "bg-[#EEF0F4] text-[#4A5578]" };

const SECTIONS = [
  { key: "main", items: ["/my-dashboard", "/"] },
  { key: "patients", title: "Patients", tone: "none", items: ["/my-patients", "/patients", "/assignments", "/testing-rotation", "/medication-requests"] },
  { key: "appointments", title: "Appointments", tone: "green", items: ["/appointments"] },
  { key: "employees", title: "Employees", tone: "lavender", items: ["/employees", "/roles"] },
  { key: "locations", title: "Locations", tone: "orange", items: ["/locations"] },
  { key: "clinical", title: "Clinical Management", tone: "blue", items: ["/clinical", "/prescriptions", "/injectables", "/labs", "/tasks", "/notifications"] },
  { key: "reports", title: "Reports", tone: "green", items: ["/reports"] },
  { key: "data", title: "Data Management", tone: "blue", items: ["/import", "/audit"] },
  { key: "system", title: "System", tone: "gray", items: ["/settings"] },
];

const EMPLOYEE_SECTIONS = [
  { key: "appointments", title: "Appointments", tone: "green", items: ["/appointments"] },
  { key: "clinical", title: "Clinical Management", tone: "blue", items: ["/prescriptions"] },
  { key: "patients", title: "Patients", tone: "lavender", items: ["/testing-rotation", "/medication-requests"] },
  { key: "alerts", title: "Notifications", tone: "orange", items: ["/notifications"] },
  { key: "reports", title: "Reports", tone: "green", items: ["/reports"] },
];

const EMPLOYEE_NAV = ["/appointments", "/prescriptions", "/testing-rotation", "/medication-requests", "/notifications", "/reports"];
const CL = { "/appointments": "appointments", "/prescriptions": "prescriptions", "/injectables": "injectables", "/labs": "labs" };
const ROLE = { employee: "Wellness Navigator", front_desk: "Front Desk", admin: "System Administrator", supervisor: "Supervisor" };
const GRAD = "bg-gradient-to-r from-[#2C55C9] to-[#4A7BE6] text-white shadow-[0_4px_12px_rgba(44,85,201,0.35)]";

function Item({ to, n, tone, collapsed, active, unread, onNav, label }) {
  const Icon = n.icon;
  const circle = tone !== "none" && !active;
  return (
    <NavLink to={CL[to] ? `/clinical?tab=${CL[to]}` : to} end={to === "/"} onClick={onNav} title={collapsed ? label : undefined}
      data-testid={`nav-${to.replace("/", "") || "dashboard"}`}
      className={`group flex items-center gap-3 rounded-xl px-2 py-1.5 text-[13.5px] font-medium transition-colors duration-150 ${collapsed ? "justify-center" : ""} ${active ? GRAD : "text-[#20336F] hover:bg-[#EEF4FF]"}`}>
      <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${circle ? TONES[tone] : active ? "" : "text-[#2F4BA8]"}`}>
        <Icon size={18} strokeWidth={1.9} />
        {collapsed && unread > 0 && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-600" />}
      </span>
      {!collapsed && <>
        <span className="flex-1 truncate">{label}</span>
        {unread > 0 ? <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white" data-testid="nav-unread-count">{unread}</span>
          : <ChevronRight size={15} className={active ? "text-white" : "text-[#8A93AD] transition-transform group-hover:translate-x-0.5"} />}
      </>}
    </NavLink>
  );
}

function SideNav({ onNav, collapsed, unread }) {
  const { user, can, t } = useApp();
  const loc = useLocation();
  const [closed, setClosed] = useState(() => JSON.parse(localStorage.getItem("navClosed") || "{}"));
  useEffect(() => { localStorage.setItem("navClosed", JSON.stringify(closed)); }, [closed]);
  const clinTab = loc.pathname === "/clinical" ? new URLSearchParams(loc.search).get("tab") : null;
  const visible = (to) => {
    const n = NAV[to];
    return (n.perm === "clinical" || can(n.perm)) && (user.role !== "employee" || EMPLOYEE_NAV.includes(to))
      && !(n.adminOnly && user.role !== "admin") && !(n.nonAdmin && user.role === "admin") && !(n.supervisorOnly && user.role !== "supervisor");
  };
  const isActive = (to) => {
    if (CL[to]) return clinTab === CL[to];
    if (to === "/clinical") return loc.pathname === "/clinical" && clinTab !== "appointments";
    return to === "/" ? loc.pathname === "/" : loc.pathname === to || loc.pathname.startsWith(`${to}/`);
  };
  return (
    <nav className={`flex flex-col gap-0.5 pb-4 ${collapsed ? "px-2" : "px-3"}`} data-testid="sidebar-nav">
      {(user.role === "employee" ? EMPLOYEE_SECTIONS : SECTIONS).map((s) => {
        const items = s.items.filter(visible);
        if (!items.length) return null;
        const multi = items.length > 1 && s.title;
        const shut = multi && closed[s.key] && !items.some(isActive);
        return (
          <div key={s.key} className={s.title ? "mt-2.5" : ""} data-testid={`nav-section-${s.key}`}>
            {s.title && (collapsed ? <div className="mx-3 my-1.5 border-t border-[#E3EAF6]" /> : (
              <button type="button" disabled={!multi} onClick={() => setClosed({ ...closed, [s.key]: !closed[s.key] })} data-testid={`nav-section-toggle-${s.key}`}
                className="mb-1 flex w-full items-center justify-between px-2 py-1 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-[#4A5578] disabled:cursor-default">
                {t(s.title)}{multi && (shut ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
              </button>
            ))}
            {!shut && items.map((to) => {
              const n = NAV[to];
              const label = t(typeof n.label === "string" ? n.label : n.label[user.role] || n.label.admin);
              return <Item key={to} to={to} n={n} tone={s.tone || "none"} collapsed={collapsed} active={isActive(to)} onNav={onNav} label={label}
                unread={to === "/notifications" ? unread : 0} />;
            })}
          </div>
        );
      })}
    </nav>
  );
}

function Brand({ collapsed, onToggle }) {
  return (
    <div className={`relative shrink-0 rounded-b-[36px] bg-gradient-to-b from-[#E3EEFF] to-[#F5F9FF] pb-4 pt-4 ${collapsed ? "px-1.5" : "px-3"}`} data-testid="sidebar-header">
      <NavLink to="/" className="block" data-testid="brand-home-link">
        <img src="/brand/logo.png" alt="Latinos Salud" className={`mx-auto w-auto object-contain transition-all ${collapsed ? "h-8" : "h-14"}`} data-testid="brand-logo" />
        {!collapsed && <div className="mt-1.5 text-center text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F4BA8]">PrEP Monitoring</div>}
      </NavLink>
      {onToggle && (
        <button onClick={onToggle} className={`hidden h-7 w-7 items-center justify-center rounded-full border border-[#DCE5F5] bg-white text-[#2F4BA8] shadow-sm transition-colors hover:bg-[#EEF4FF] lg:flex ${collapsed ? "mx-auto mt-2" : "absolute right-2.5 top-2.5"}`}
          data-testid="sidebar-collapse-btn" aria-label="Toggle sidebar">{collapsed ? <PanelLeftOpen size={13} /> : <PanelLeftClose size={13} />}</button>
      )}
    </div>
  );
}

function Profile({ collapsed, onNav }) {
  const { user, logout, t } = useApp();
  const nav = useNavigate();
  const initials = (user.name || "?").replace(/\(.*\)/, "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="shrink-0 border-t border-[#E3EAF6] p-2.5">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button className={`flex w-full items-center gap-2.5 rounded-xl p-1.5 text-left transition-colors hover:bg-[#EEF4FF] ${collapsed ? "justify-center" : ""}`} data-testid="sidebar-profile-btn">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2C55C9] to-[#4A7BE6] text-xs font-bold text-white">{initials}</span>
            {!collapsed && <>
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-[13px] font-semibold text-[#20336F]" data-testid="sidebar-user-name">{user.name}</span>
                <span className="block truncate text-[11px] text-[#6B7490]" data-testid="sidebar-user-role">{ROLE[user.role] || user.role}</span>
              </span>
              <ChevronDown size={15} className="text-[#8A93AD]" />
            </>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-52">
          <DropdownMenuItem onSelect={() => { nav("/account"); onNav && onNav(); }} data-testid="nav-account"><UserCircle size={14} className="mr-2" />{t("My Account")}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={logout} className="text-red-600" data-testid="sidebar-logout"><LogOut size={14} className="mr-2" />{t("Sign out")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function Sidebar({ collapsed, onToggle, onNav, unread }) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#DCE5F5] bg-white shadow-[0_6px_24px_rgba(32,51,111,0.08)]">
      <Brand collapsed={collapsed} onToggle={onToggle} />
      <div className="sidebar-scroll min-h-0 flex-1 overflow-y-auto pt-3"><SideNav collapsed={collapsed} onNav={onNav} unread={unread} /></div>
      <Profile collapsed={collapsed} onNav={onNav} />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, t, lang, setLang, dark, toggleDark, logout, preview, stopPreview } = useApp();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("navCollapsed") === "1");
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, [loc.pathname]);
  const toggle = () => { localStorage.setItem("navCollapsed", collapsed ? "0" : "1"); setCollapsed(!collapsed); };

  return (
    <div className="min-h-screen bg-background">
      <aside className={`fixed inset-y-0 left-0 z-30 hidden p-2.5 transition-[width] duration-200 lg:block ${collapsed ? "w-[88px]" : "w-[288px]"}`} data-testid="sidebar">
        <Sidebar collapsed={collapsed} onToggle={toggle} unread={unread} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[300px] max-w-[88vw] p-2.5" data-testid="mobile-sidebar">
            <button className="absolute right-4 top-4 z-10 rounded-full bg-white p-1 text-[#20336F] shadow" onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X size={18} /></button>
            <Sidebar onNav={() => setOpen(false)} unread={unread} />
          </aside>
        </div>
      )}
      <div className={`transition-[padding] duration-200 ${collapsed ? "lg:pl-[88px]" : "lg:pl-[288px]"}`}>
        {preview && (
          <div className="no-print flex flex-wrap items-center justify-between gap-2 bg-amber-100 px-4 py-2 text-sm text-amber-900" data-testid="preview-banner">
            <span className="flex items-center gap-2"><Eye size={15} /> {t("Previewing as")} <b>{preview.name}</b> — read-only</span>
            <button className="btn btn-sm btn-ghost" onClick={() => { stopPreview(); nav("/employees"); }} data-testid="exit-preview-btn">{t("Exit preview")}</button>
          </div>
        )}
        <header className="no-print sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-[var(--panel)]/95 px-3 backdrop-blur md:gap-3 md:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-nav-open"><Menu size={20} /></button>
          {user.role === "employee" && <NavLink to="/" end className={({ isActive }) => `btn btn-sm ${isActive ? "btn-primary" : "btn-ghost"}`} data-testid="header-dashboard-link"><LayoutDashboard size={14} /><span className="hidden sm:inline">{t("Dashboard")}</span></NavLink>}
          <div className="flex-1" />
          <div className="flex overflow-hidden rounded-md border text-xs font-semibold" data-testid="lang-toggle">
            {["en", "es"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                className={`px-2.5 py-1.5 ${lang === l ? "bg-[#283F91] text-white" : "text-[var(--muted-ink)]"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={toggleDark} data-testid="theme-toggle" aria-label="Toggle theme">{dark ? <Sun size={15} /> : <Moon size={15} />}</button>
          <button className="relative btn btn-ghost btn-sm" onClick={() => nav("/notifications")} data-testid="header-notifications-btn">
            <Bell size={15} />
            {unread > 0 && <span className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 px-1.5 text-[10px] text-white" data-testid="unread-count">{unread}</span>}
          </button>
          <button className="hidden text-right leading-tight sm:block" onClick={() => nav("/account")} data-testid="header-account-btn">
            <div className="text-[13px] font-semibold text-[var(--ink)]" data-testid="current-user-name">{user.name}</div>
            <div className="text-[11px] capitalize text-[var(--muted-ink)]" data-testid="current-user-role">{user.role === "employee" ? "Wellness Navigator" : user.role === "front_desk" ? "Front Desk" : user.role}</div>
          </button>
          <button className="btn btn-ghost btn-sm" onClick={logout} data-testid="logout-btn" title={t("Sign out")}><LogOut size={15} /></button>
        </header>
        <main className="fade-in mx-auto max-w-[1600px] px-4 py-6 md:px-8 md:py-8">{children}</main>
      </div>
    </div>
  );
}
